import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { trackUpgradePlanClick } from '../../../services/analytics.service';
import { getUserProfile } from '../../../services/userService';
import { logger } from '../../../utils/logger';

/** Usage tab — displays credit balance, subscription info, and interaction stats */
const SettingsUsage: React.FC = () => {
  const { t } = useTranslation();
  const { subscription, credits, isLoading: subLoading, refreshSubscription } = useSubscription();
  const [totalInteractions, setTotalInteractions] = useState<number>(0);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    const initData = async () => {
      try {
        // Run sync before profile call to ensure credits are up to date
        // This ensures the user sees the most updated credits when landing on the Usage tab
        await refreshSubscription();

        const profile = await getUserProfile();
        setTotalInteractions(profile.total_interactions ?? 0);
      } catch (error) {
        logger.error(
          'Settings',
          'Failed to initialize usage data',
          error instanceof Error ? error : new Error(String(error)),
        );
      } finally {
        setProfileLoading(false);
      }
    };
    initData();
  }, [refreshSubscription]);

  /** Render a progress bar with percentage */
  const renderProgressBar = (used: number, total: number | null, color: string) => {
    if (total === null || total === 0) {
      return (
        <div className="usage-progress-bar">
          <div className="usage-progress-fill" style={{ width: '0%', background: color }} />
        </div>
      );
    }
    const percentage = Math.min((used / total) * 100, 100);
    return (
      <div className="usage-progress-bar">
        <div
          className="usage-progress-fill"
          style={{ width: `${percentage}%`, background: color }}
        />
      </div>
    );
  };

  /** Format credit values to avoid floating point precision issues (e.g. 30.40000000000001 -> 30.4) */
  const formatCredit = (val: number | null | undefined): string | number => {
    if (val === null || val === undefined) return 0;
    return Number(val.toFixed(1));
  };

  /** Format the tier name for display */
  const formatTierName = (tier: string): string => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  /** Get tier badge color */
  const getTierColor = (tier: string): string => {
    switch (tier) {
      case 'premium':
        return '#8b5cf6';
      case 'plus':
        return '#3b82f6';
      case 'basic':
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  if (subLoading || profileLoading) {
    return (
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.usage', 'Usage')}</h2>
        <div className="usage-loading">
          <div className="spinner" />
          <span>{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">{t('settings.usage', 'Usage')}</h2>

      {/* Subscription Plan Card */}
      <div className="usage-card">
        <div className="usage-card-header">
          <h3>{t('settings.currentPlan', 'Current Plan')}</h3>
          <span
            className="usage-tier-badge"
            style={{ background: getTierColor(subscription?.tier ?? 'free') }}
          >
            {formatTierName(subscription?.tier ?? 'free')}
          </span>
        </div>
        <div className="usage-card-body">
          <div className="usage-stat-row">
            <span className="usage-stat-label">{t('settings.status', 'Status')}</span>
            <span className={`usage-stat-value ${subscription?.isActive ? 'active' : 'inactive'}`}>
              {subscription?.isActive
                ? t('settings.active', 'Active')
                : t('settings.inactive', 'Inactive')}
            </span>
          </div>
          {subscription?.expiresAt && (
            <div className="usage-stat-row">
              <span className="usage-stat-label">{t('settings.expiresOn', 'Expires on')}</span>
              <span className="usage-stat-value">
                {new Date(subscription.expiresAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
        {(!subscription?.isActive || subscription?.tier === 'free') && (
          <Link
            to="/subscription"
            className="usage-upgrade-link"
            onClick={() =>
              trackUpgradePlanClick({
                source: 'settings_usage',
                currentPlan: subscription?.tier || 'free',
              })
            }
          >
            {t('settings.upgradePlan', 'Upgrade Plan')}
          </Link>
        )}
      </div>

      {/* Credit Balance Card */}
      <div className="usage-card">
        <div className="usage-card-header">
          <h3>{t('settings.creditBalance', 'Credit Balance')}</h3>
        </div>
        <div className="usage-card-body">
          {/* Total Credits */}
          <div className="usage-credit-block">
            <div className="usage-credit-row">
              <span className="usage-credit-label">
                {t('settings.totalCredits', 'Total Credits')}
              </span>
              <span className="usage-credit-value">{formatCredit(credits?.credits.total)}</span>
            </div>
          </div>

          {/* Subscription Credits */}
          <div className="usage-credit-block">
            <div className="usage-credit-row">
              <span className="usage-credit-label">
                {t('settings.subscriptionCredits', 'Subscription Credits')}
              </span>
              <span className="usage-credit-value">
                {formatCredit(credits?.credits.subscription)}
              </span>
            </div>
          </div>

          {/* Addon Credits */}
          <div className="usage-credit-block">
            <div className="usage-credit-row">
              <span className="usage-credit-label">
                {t('settings.addonCredits', 'Add-on Credits')}
              </span>
              <span className="usage-credit-value">{formatCredit(credits?.credits.addon)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Text Usage Card */}
      <div className="usage-card">
        <div className="usage-card-header">
          <h3>{t('settings.dailyTextUsage', 'Daily Text Usage')}</h3>
        </div>
        <div className="usage-card-body">
          <div className="usage-credit-block">
            <div className="usage-credit-row">
              <span className="usage-credit-label">{t('settings.used', 'Used')}</span>
              <span className="usage-credit-value">
                {credits?.daily_text.used ?? 0}
                {credits?.daily_text.limit !== null && (
                  <span className="usage-credit-total"> / {credits?.daily_text.limit}</span>
                )}
              </span>
            </div>
            {renderProgressBar(
              credits?.daily_text.used ?? 0,
              credits?.daily_text.limit ?? null,
              '#3b82f6',
            )}
          </div>
          {credits?.daily_text.remaining !== null && (
            <div className="usage-stat-row">
              <span className="usage-stat-label">{t('settings.remaining', 'Remaining')}</span>
              <span className="usage-stat-value">{credits?.daily_text.remaining}</span>
            </div>
          )}
        </div>
      </div>

      {/* Interactions Card */}
      <div className="usage-card">
        <div className="usage-card-header">
          <h3>{t('settings.interactions', 'Interactions')}</h3>
        </div>
        <div className="usage-card-body">
          <div className="usage-stat-row">
            <span className="usage-stat-label">
              {t('settings.totalInteractions', 'Total Interactions')}
            </span>
            <span className="usage-stat-value">{profileLoading ? '...' : totalInteractions}</span>
          </div>
        </div>
      </div>

      {/* Feature Access Card */}
      <div className="usage-card">
        <div className="usage-card-header">
          <h3>{t('settings.featureAccess', 'Feature Access')}</h3>
        </div>
        <div className="usage-card-body">
          <div className="usage-feature-grid">
            <div
              className={`usage-feature-item ${credits?.features.voice_video ? 'enabled' : 'disabled'}`}
            >
              <div className="usage-feature-info">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Video Icon</title>
                  <polygon points="23 7 16 12 23 17 23 7" />
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                </svg>
                <span>{t('settings.liveStreaming', 'Live Streaming')}</span>
              </div>
              <span
                className={`usage-feature-status ${credits?.features.voice_video ? 'enabled' : 'disabled'}`}
              >
                {credits?.features.voice_video
                  ? t('settings.enabled', 'Enabled')
                  : t('settings.disabled', 'Disabled')}
              </span>
            </div>
            <div
              className={`usage-feature-item ${credits?.features.text ? 'enabled' : 'disabled'}`}
            >
              <div className="usage-feature-info">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Text Icon</title>
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <span>{t('settings.textChat', 'Text Chat')}</span>
              </div>
              <span
                className={`usage-feature-status ${credits?.features.text ? 'enabled' : 'disabled'}`}
              >
                {credits?.features.text
                  ? t('settings.enabled', 'Enabled')
                  : t('settings.disabled', 'Disabled')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsUsage;
